import { BASE_URL } from "@/constants/endpoints";
import { createAxiosWithAuth } from "@/utils/axios-config";
import { objectToQueryStringWithArray } from "@/utils/query-string";
import { AxiosInstance } from "axios";

let productsApiPromise: Promise<AxiosInstance> | null = null;

const getProductsApi = () => {
  if (productsApiPromise) return productsApiPromise;
  productsApiPromise = createAxiosWithAuth(BASE_URL, "jwt");
  return productsApiPromise;
};

export interface DeleteProductsProps {
  productos: number[];
}

export interface GetSimilarProductsProps {
  nombre: string;
  excluidos?: number[];
}

export interface CreateProductProps {
  nombre: string;
  descripcion: string;
  numAdmis?: string;
  tipoProducto: number;
}

export interface UpdateProductProps {
  id: number;
  nombre: string;
  tipo: "fertilizante" | "combustible";
}

export interface EditProductProps {
  id: number;
  nombre: string;
  descripcion: string;
  numAdmis?: string;
  tipoProducto: number;
  activo: boolean;
}

export interface GetProductDetailProps {
  id: number;
}

export interface GetProductsFiltersProps {
  nombre?: string;
  descripcion?: string
  activo?: boolean
  tipoProducto?: number
}

// Fertilizantes
export const getFertilizantes = async (filters?: GetProductsFiltersProps) => {
  const api = await getProductsApi();
  if (!api) return;

  const queryString = filters ? objectToQueryStringWithArray(filters) : "";
  const url = queryString ? `/Productos/fertilizantes?${queryString}` : "/Productos/fertilizantes";
  const response = await api.get(url);
  return response.data;
};

export const createProduct = async (props: CreateProductProps) => {
  const api = await getProductsApi();
  if (!api) return;

  const response = await api.post(`/Productos`, props);
  return response.data;
};

export const createFertilizante = async (props: Omit<CreateProductProps, 'tipoProducto'>) => {
  return createProduct({ ...props, tipoProducto: 1 });
};

export const createCombustible = async (props: Omit<CreateProductProps, 'tipoProducto'>) => {
  return createProduct({ ...props, tipoProducto: 2 });
};

export const updateFertilizante = async (props: Omit<UpdateProductProps, 'tipo'>) => {
  const api = await getProductsApi();
  if (!api) return;

  const response = await api.put(
    `/Productos/fertilizantes/${props.id}`,
    { ...props, tipo: "fertilizante" }
  );
  return response.data;
};

export const deleteProducts = async (props: DeleteProductsProps) => {
  const api = await getProductsApi();
  if (!api) return;

  await api.delete(`/Productos?${objectToQueryStringWithArray(props)}`);
};

// Combustibles
export const getCombustibles = async (filters?: GetProductsFiltersProps) => {
  const api = await getProductsApi();
  if (!api) return;

  const queryString = filters ? objectToQueryStringWithArray(filters) : "";
  const url = queryString ? `/Productos/combustibles?${queryString}` : "/Productos/combustibles";
  const response = await api.get(url);
  return response.data;
};


export const updateCombustible = async (props: Omit<UpdateProductProps, 'tipo'>) => {
  const api = await getProductsApi();
  if (!api) return;

  const response = await api.put(
    `/Productos/combustibles/${props.id}`,
    { ...props, tipo: "combustible" }
  );
  return response.data;
};

// Aliases for backward compatibility
export const deleteFertilizantes = deleteProducts;
export const deleteCombustibles = deleteProducts;

// Funciones generales para productos
export const getProductDetail = async (props: GetProductDetailProps) => {
  const api = await getProductsApi();
  if (!api) return;

  const response = await api.get(
    `/Productos/${props.id}`
  );
  return response.data;
};

export const editProduct = async (props: EditProductProps) => {
  const api = await getProductsApi();
  if (!api) return;

  const response = await api.patch(`/Productos?${objectToQueryStringWithArray(props)}`);
  return response.data;
};

